import { ChangeEvent, Fragment, useContext, useState } from 'react';

import { EventResponse } from 'generated/index';

import {
  ContextMenuContainer,
  EventTooltip,
  PopoverWarningDialog
} from '../../components';

import {
  getAddressNotFound,
  getClientName,
  getSelectableStatus
} from '../../utils';

import { Box, Checkbox, styled, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';

import { getGridTemplateColumns } from '../ListView';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  dialogActiveAtom,
  selectedContextMenuOptionAtom,
  shouldShowPopoverDialogAtom,
  assignEmploymentEditAppointmentTypeAtom
} from '../../atoms';
import { FormattedTimeInput } from '../../components/ActivityCard/components';
import { formatHoursMinutes } from 'utils/date';
import { t } from 'i18next';
import dayjs from 'dayjs';
import { ListViewContext } from '../ListViewContext';
import { EditAppointmentType } from 'RestAPIs/activities/types';
import { mutations } from 'RestAPIs/rapini';
import LoadingOverlay from 'components/shared/LoadingOverlay';
import showSnackbar, { showErrorSnackbar } from 'components/shared/Snackbar';
import { useQueryClient } from 'react-query';
import { SignalRow } from '../../components/SignalRow';

const Row = styled(Box)`
  position: relative;
  display: grid;
  grid-auto-flow: column;
  margin-bottom: 8px;
  min-height: 25px;
  border: 1px solid #efefef;
  border-radius: 4px;
  background-color: #f7f7f7;
  font-family: 'signikaregular';
  font-size: 13px;
`;

interface EventRowProps {
  columns: GridColDef[];

  event: EventResponse;
  onCheckboxChange: (
    e: ChangeEvent<HTMLInputElement>,
    value: string[] | string
  ) => void;
  checked: string[];
}

const EventRow = ({
  columns,
  event,
  onCheckboxChange,
  checked
}: EventRowProps) => {
  const { mutateAsync: updateAppointment, isLoading: updateIsLoading } =
    mutations.useUpdateAppointment();
  const queryClient = useQueryClient();

  const [isHovering, setIsHovering] = useState<boolean>(false);
  const dialogActive = useRecoilValue<boolean>(dialogActiveAtom);

  const [, setAssignEmploymentEditAppointmentType] = useRecoilState<
    EditAppointmentType | null | undefined
  >(assignEmploymentEditAppointmentTypeAtom);
  const [, setSelectedContextMenuOption] = useRecoilState(
    selectedContextMenuOptionAtom
  );

  const [
    {
      id: popoverOccurenceId,
      isUnassignment,
      scheduleStart: popoverScheduleStart,
      isAssignEmployment
    },
    setShouldShowPopoverDialog
  ] = useRecoilState(shouldShowPopoverDialogAtom);

  const { isLoading, onClose, onHandleDisconnectAppointmentConfirmation } =
    useContext(ListViewContext);

  const [contextMenuActive, setContextMenuActive] = useState<boolean>(false);

  const {
    id,
    activity,
    appointmentGrouping,
    clients,
    description,
    durationInSeconds,
    employees,
    schedulingLabel,
    isAbsent,
    isRecurring,
    organizationalUnit,
    project,
    start,
    status,
    timeWindow,
    location,
    warnings: {
      startsTooEarly,
      startsTooLate,
      isInSync,
      hasOverlap,
      isOverQualified: overQualified,
      isUnderQualified: underQualified,
      excludedEmployeeClientCombination
    },
    workPlace
  } = event;
  const row = {
    id,
    activity: activity?.name || '',
    client: getClientName(clients[0]),
    duration: formatHoursMinutes(durationInSeconds),
    employee: employees[0]?.nameInformal,
    project: project?.name,
    projectDescription: event.project?.description,
    status: status ? t(`activityPlanning.status.${event.status}`) : '',
    timeWindow: !event.project
      ? `${dayjs(event.start).format('HH:mm')} - ${dayjs(event.end).format(
          'HH:mm'
        )}`
      : '',
    workplace: workPlace?.name,
    workPlaceDescription: workPlace?.description,
    description,
    durationInSeconds,
    appointmentGrouping: appointmentGrouping?.description,
    organizationalUnit: event.organizationalUnit?.name,
    recurrenceRule: event.recurrenceRule,
    isSelectable: getSelectableStatus(event)
  };

  const tabbableFields: string[] = ['duration'];

  const addressNotFound = getAddressNotFound(location, activity);

  const hasScheduleStart = !!(popoverScheduleStart && start);

  // We compare only the YYYY-MM-DD of the dates, not the time.
  const popoverScheduleStartDate: string = dayjs(popoverScheduleStart).isValid()
    ? dayjs(popoverScheduleStart).format('YYYY-MM-DD')
    : '';
  const currentScheduleStartDate: string = dayjs(start).isValid()
    ? dayjs(start).format('YYYY-MM-DD')
    : '';

  const showPopoverDialog: boolean =
    id === popoverOccurenceId &&
    (hasScheduleStart
      ? popoverScheduleStartDate === currentScheduleStartDate
      : true);

  const allowToolTip: boolean =
    isHovering && !contextMenuActive && !dialogActive;

  const loading = isLoading || updateIsLoading;

  const handleUpdateAppointment = async (
    durationInSeconds: number,
    editAppointmentType: EditAppointmentType = 'ONLY_THIS'
  ) => {
    const request = {
      activityEntityId: activity.entityId,
      appointmentGroupingEntityId: appointmentGrouping?.entityId,
      appointmentGroupingStart: appointmentGrouping?.start,
      description,
      durationInSeconds,
      editAppointmentType,
      entityId: event.id,
      employeeEntityId: employees[0]?.entityId,
      organizationalUnitId: organizationalUnit?.id,
      originalOccurrenceStart: start,
      projectEntityId: project?.entityId,
      schedulingLabel,
      start,
      suppressWarnings: false
    };

    try {
      await updateAppointment(request);
      showSnackbar({ message: t('changesSaved') });
      queryClient.invalidateQueries('planning');
    } catch (err) {
      showErrorSnackbar();
    }
  };

  return (
    <Tooltip
      enterNextDelay={500}
      disableHoverListener={!allowToolTip}
      sx={{
        zIndex: 0
      }}
      title={allowToolTip && <EventTooltip event={event} />}
    >
      <Row
        sx={{
          height: 50,
          gridTemplateColumns: getGridTemplateColumns(columns)
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {columns.map((column, i) => {
          if (column.field === 'signal') {
            return (
              <Box
                key={`${i}-${column.field}}-signal`}
                display="flex"
                alignItems="center"
                sx={{
                  width: column.width
                }}
              >
                <SignalRow
                  schedulingLabel={schedulingLabel}
                  addressNotFound={addressNotFound}
                  description={description}
                  excludedEmployeeClientCombination={
                    excludedEmployeeClientCombination
                  }
                  hasOverlap={hasOverlap}
                  isAbsent={isAbsent}
                  isInSyncWithEcd={isInSync}
                  isRecurring={isRecurring}
                  locationType={location?.locationType}
                  overQualified={overQualified}
                  selected={checked.includes(row.id)}
                  signalThreshold={4}
                  startsTooEarly={startsTooEarly}
                  startsTooLate={startsTooLate}
                  timeWindow={timeWindow}
                  underQualified={underQualified}
                />
              </Box>
            );
          }

          return (
            <Fragment key={`${i}-${row.id}`}>
              {row.isSelectable && i === 0 ? (
                <Checkbox
                  checked={checked.includes(row.id)}
                  onChange={(e) => onCheckboxChange(e, row.id)}
                />
              ) : i === 0 ? (
                <Box width={40} />
              ) : null}
              <Box
                display="flex"
                alignItems="center"
                width={column.width}
                sx={{
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  textDecoration: isAbsent ? 'line-through' : 'initial',
                  overflow: 'hidden',
                  minWidth: 0
                }}
              >
                <Box
                  sx={{
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden'
                  }}
                >
                  {tabbableFields.includes(column.field) && !!row.project ? (
                    <FormattedTimeInput
                      isEditable={event.isEditable}
                      isCredit={event.isCredit}
                      value={
                        durationInSeconds
                          ? formatHoursMinutes(durationInSeconds)
                          : ''
                      }
                      onFinished={(durationInSeconds: number) => {
                        if (durationInSeconds === event.durationInSeconds)
                          return;
                        handleUpdateAppointment(durationInSeconds);
                      }}
                    />
                  ) : (
                    row[column.field]
                  )}
                </Box>
              </Box>
            </Fragment>
          );
        })}

        <PopoverWarningDialog
          open={showPopoverDialog && !loading}
          onClose={() => {
            onClose?.();

            if (isAssignEmployment) {
              setShouldShowPopoverDialog({ id: '' });
              setSelectedContextMenuOption(null);
              setAssignEmploymentEditAppointmentType(null);
            }
          }}
          onClickAppointment={(editAppointmentType: EditAppointmentType) => {
            if (isUnassignment)
              onHandleDisconnectAppointmentConfirmation?.(editAppointmentType);
            if (isAssignEmployment)
              setAssignEmploymentEditAppointmentType(editAppointmentType);
            setShouldShowPopoverDialog({ id: '' });
          }}
        >
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <ContextMenuContainer
              eventId={event.id}
              clientId={event.clients[0]?.id}
              eventIsOpen={event.isOpen}
              hasAppointmentGrouping={!!event.appointmentGrouping}
              hasEmployee={!!event.employees?.[0]}
              isAssigned={!!event.isAssigned}
              isAbsent={event.isAbsent}
              isEditable={event.isEditable}
              isCreditable={event.isCreditable}
              isCredit={event.isCredit}
              schedulingLabel={event.schedulingLabel}
              isProject={!!event.project}
              isWorkPlace={!!event.workPlace}
              onOpen={(open: boolean) => setContextMenuActive(open)}
            />
          </Box>
        </PopoverWarningDialog>

        <LoadingOverlay loading={loading} />
      </Row>
    </Tooltip>
  );
};

export default EventRow;
